import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";

const PAGE_SIZE = 5;

export default function NewsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get("q") ?? "";

  const [keyword, setKeyword] = useState(urlQuery);
  const [submitted, setSubmitted] = useState(urlQuery);
  const [page, setPage] = useState(1);

  usePageMeta({
    title: "三级片资讯 - 行业观察、影片动态与幕后报道",
    description:
      "三级片资讯频道，提供行业观察、影片动态、专题报道与幕后访谈，按发布时间倒序更新影视内容相关新闻与深度文章。",
    keywords: "三级片资讯,三级片新闻,影视资讯,影片动态,行业观察",
  });

  useEffect(() => {
    setKeyword(urlQuery);
    setSubmitted(urlQuery);
    setPage(1);
  }, [urlQuery]);

  const filtered = useMemo(() => {
    const key = submitted.trim().toLowerCase();
    const sorted = [...newsList].sort((a, b) => b.date.localeCompare(a.date));
    if (!key) return sorted;
    return sorted.filter((item) =>
      [item.title, item.summary, item.category, item.author]
        .join(" ")
        .toLowerCase()
        .includes(key)
    );
  }, [submitted]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setSubmitted(value);
    setPage(1);
    setSearchParams(value ? { q: value } : {});
  };

  const handleReset = () => {
    setKeyword("");
    setSubmitted("");
    setPage(1);
    setSearchParams({});
  };

  return (
    <SiteLayout>
      <div className="rise-in">
        <Breadcrumb items={[{ label: "影视资讯" }]} />

        <header className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-accent-600">
              <span className="inline-block h-[2px] w-6 bg-accent-500" />
              News & Editorial
            </p>
            <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-[32px]">
              三级片影视资讯
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              行业观察、影片动态、专题报道与幕后访谈。所有内容按发布时间倒序排列，支持关键词检索。
            </p>
          </div>
          <span className="rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-500">
            共 {newsList.length} 篇资讯
          </span>
        </header>

        <form onSubmit={handleSubmit} className="mt-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex h-11 w-full items-center overflow-hidden rounded-md border border-background-300 bg-background-50 focus-within:border-primary-400">
              <i className="ri-search-line ml-3 text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                name="q"
                maxLength={50}
                placeholder="输入关键词，例如「修复」「悬疑」「都市」"
                aria-label="搜索资讯关键词"
                className="min-w-0 flex-1 bg-transparent px-2.5 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword("")}
                  aria-label="清空关键词"
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-foreground-400 hover:bg-background-100"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-11 shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索资讯
            </button>
          </div>
          {submitted && (
            <p className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              关键词「<span className="text-primary-700">{submitted}</span>」共找到 {filtered.length} 条结果
              <button
                type="button"
                onClick={handleReset}
                className="cursor-pointer rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:bg-background-200"
              >
                清除搜索
              </button>
            </p>
          )}
        </form>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-6 flex flex-col gap-4">
              {pageItems.map((item) => (
                <article
                  key={item.id}
                  className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
                >
                  <Link
                    to={`/news/${item.id}`}
                    className="flex cursor-pointer flex-col gap-4 p-4 sm:flex-row md:p-5"
                  >
                    <div className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-[124px] sm:w-[196px]">
                      <img
                        src={item.cover}
                        alt={`${item.title} 资讯配图`}
                        title={`${item.title} - 三级片影视资讯`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-[11px]">
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">
                          {item.category}
                        </span>
                        <span className="flex items-center gap-1 text-foreground-400">
                          <i className="ri-calendar-line" />
                          {item.date}
                        </span>
                        <span className="text-foreground-400">·</span>
                        <span className="text-foreground-400">{item.author}</span>
                      </div>
                      <h2 className="mt-2 line-clamp-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                        {item.title}
                      </h2>
                      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                        {item.summary}
                      </p>
                      <span className="mt-3 flex items-center gap-1 text-xs text-primary-600">
                        阅读全文
                        <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <i className="ri-newspaper-line text-4xl text-background-400" />
            <h2 className="mt-4 text-lg font-bold text-foreground-950">暂无相关新闻</h2>
            <p className="mt-2 text-sm text-foreground-600">
              没有找到与「{submitted || "关键词"}」匹配的资讯，换个关键词试试。
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line" />
              查看全部资讯
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}